import { BoltFixProps } from 'features/bolt-fix/types'
import Hero from 'sections/hero/Hero'
import { useTranslation } from 'next-i18next/pages'
import classNames from 'classnames'
import styles from './BoltFix.module.scss'
import BlocksRenderer from 'components/blocks-renderer/BlocksRenderer'
import { Button, Icon, Testimonials } from '@boltenergy-be/design-system'
import { routes } from 'utils/routes'
import { useRouter } from 'next/router'
import { startSimulationOrRegistration } from 'utils/customerFlow'
import EncyclopediaArticlesReference from 'sections/encyclopedia-articles-reference/EncyclopediaArticlesReference'
import ProductInfo from 'sections/product-info/ProductInfo'
import { QUOTES } from 'features/producer-contact/ProducerContact.constants'
import { Heading, Tag } from '@boltenergy-be/design-system'
import Link from 'components/link/Link'
import FaqWithDefaultDescription from 'components/faq-with-default-description/FaqWithDefaultDescription'
import BoltOffering from 'sections/bolt-offering/BoltOffering'
import { BackgroundColors } from 'types/layout'
import StartSimulationCard from 'components/start-simulation-card/StartSimulationCard'
import { PRODUCTS } from '@boltenergy-be/shared'

const BoltFix = ({ content, articles }: BoltFixProps) => {
  // i18n
  const { t } = useTranslation(['pricing', 'common', 'generator'])

  // Router
  const { locale } = useRouter()

  // Constants
  const { advantages, explanation, faq_category, hero, product } = content

  return (
    <main>
      {/* HERO */}
      {hero && (
        <Hero
          image={{
            alt: 'Producer Kris',
            caption: { extra: hero.heroImage?.extraCaption, text: hero?.heroImage?.caption },
            publicId: hero.heroImage?.image.provider_metadata.public_id
          }}
          backgroundColor={BackgroundColors.ACCENT_WARM}
          color="dark"
        >
          <div>
            {product?.name && (
              <Tag type="positive" className="mb-200">
                Bolt {product.name}
              </Tag>
            )}
            <Hero.Title value={hero.title} />
          </div>
          <Hero.Paragraph value={hero.description} />
        </Hero>
      )}

      <section className="section container">
        <StartSimulationCard specificProduct={PRODUCTS.fix} fullWidth />
      </section>

      {/* OFFERING */}
      <BoltOffering title={t('pricing:offering.title')} />

      {/* ADVANTAGES */}
      {advantages && (
        <section className={classNames('section container', styles.advantages)}>
          <div className={styles.wrapper}>
            <Heading as="h2" variant="d3">
              {advantages.title}
            </Heading>
            <BlocksRenderer
              content={advantages.content}
              blocks={{
                'list-item': ({ children }) => (
                  <li>
                    <Icon name="badgeCheck" />
                    {children}
                  </li>
                )
              }}
            />
            <div className={styles['button-wrapper']}>
              <Button
                variant="primary"
                className={styles.cta}
                onClick={() => startSimulationOrRegistration({ specificProduct: PRODUCTS.fix })}
              >
                {advantages.buttonText}
              </Button>
              <Link representation="button" variant="secondary" href={routes(locale).pricing}>
                {t('common:viewAllTariffs', 'Bekijk alle tarieven')}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* INFO SECTION */}
      {explanation && (
        <ProductInfo
          isDark
          startRegistration={() => startSimulationOrRegistration({ specificProduct: PRODUCTS.fix })}
          contentBlocks={explanation}
          product={PRODUCTS.fix}
        />
      )}

      {/* ENCYCLOPEDIA */}
      {!!articles?.length && <EncyclopediaArticlesReference title={t('encyclopedia.title')} articles={articles} />}

      {/* TESTIMONIALS */}
      <Testimonials
        color="accent-vibrant"
        entries={QUOTES.map((quote) => ({
          ...quote,
          quote: t(quote.quote),
          caption:
            'caption' in quote
              ? {
                  author: quote.caption.author ? t(quote.caption.author) : undefined,
                  extra: quote.caption.extra ? t(quote.caption.extra) : undefined
                }
              : undefined
        }))}
      />

      {/* FAQ */}
      <FaqWithDefaultDescription categories={faq_category} title={t('pricing:faq.title')} />
    </main>
  )
}

export default BoltFix
